import { useState } from "react";

type ColumnType = "date" | "default";
type SortDirection = "asc" | "desc";

const getColumnType = <T extends object>(initialData: T[], orderBy: keyof T): ColumnType => {
    const isDateColumn = initialData.every((item) => {
        const value = item[orderBy];

        return value === "" ? true : !isNaN(Date.parse(value as string));
    });

    return isDateColumn ? "date" : "default";
};

const getSortValue = <T extends object>(value: T[keyof T], columnType: ColumnType) =>
    columnType === "date" && typeof value === "string" ? Date.parse(value) || 0 : value;

export const useSorting = <T extends object>(
    initialData: T[],
    initialOrderBy: keyof T,
    initialOrderDirection: SortDirection = "desc"
) => {
    const [orderBy, setOrderBy] = useState<keyof T>(initialOrderBy);
    const [orderDirection, setOrderDirection] = useState<SortDirection>(initialOrderDirection);
    const orderByColumnType = getColumnType(initialData, orderBy);

    const handleSort = (column: keyof T) => {
        if (orderBy === column) {
            setOrderDirection(orderDirection === "asc" ? "desc" : "asc");
        } else {
            setOrderBy(column);
            setOrderDirection("asc");
        }
    };

    const sortedData = [...initialData].sort((a, b) => {
        const valueA = getSortValue(a[orderBy], orderByColumnType);
        const valueB = getSortValue(b[orderBy], orderByColumnType);

        if (valueA < valueB) {
            return orderDirection === "asc" ? -1 : 1;
        }

        if (valueA > valueB) {
            return orderDirection === "asc" ? 1 : -1;
        }

        return 0;
    });

    return {
        orderBy,
        orderDirection,
        handleSort,
        sortedData,
    };
};
